import { emitEvent } from "@/lib/makai-os/emit";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useLearnerContext } from "@/lib/learner-context";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { getPack } from "@/lib/offline/packs";
import { enqueue } from "@/lib/offline/outbox";
import { newId, saveProgress } from "@/lib/offline/offline-progress";
import { celebrate } from "@/components/makai/celebration";
import { Loader2, Timer, ArrowLeft, ArrowRight, Send, CheckCircle2, XCircle, Trophy, AlertCircle, Sparkles } from "lucide-react";
import { gradeQuestion, shuffle, type Question, type Answer, QUESTION_TYPE_LABEL } from "@/lib/quiz-grading";
import { useServerFn } from "@tanstack/react-start";
import { analyzeAssessment, type AdaptiveReport } from "@/lib/adaptive-engine.functions";
import { AdaptiveReportView } from "@/components/adaptive/adaptive-report";
import { recordSignals } from "@/lib/intelligence.functions";
import { generateReportCard } from "@/lib/report-card.functions";

export const Route = createFileRoute("/learner/quizzes/$quizId")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({ meta: [{ title: "Quiz — MAKAI" }] }),
  component: QuizPlayer,
});

type QuizMeta = {
  id: string; title: string; description: string | null; instructions: string | null;
  time_limit_min: number | null; total_marks: number; pass_mark: number | null;
  difficulty: string; randomize_questions: boolean; shuffle_options: boolean;
  show_feedback: boolean; show_correct_answers: boolean; max_attempts: number | null;
  subject?: { name: string } | null;
};

function slug(s: string) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "unknown";
}

function QuizPlayer() {
  const { quizId } = Route.useParams();
  const { ctx, loading } = useLearnerContext();
  const navigate = useNavigate();
  const analyze = useServerFn(analyzeAssessment);
  const sendSignals = useServerFn(recordSignals);
  const genReport = useServerFn(generateReportCard);
  const [reportId, setReportId] = useState<string | null>(null);
  const [quiz, setQuiz] = useState<QuizMeta | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [order, setOrder] = useState<number[]>([]);
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [idx, setIdx] = useState(0);
  const [started, setStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<any | null>(null);
  const [report, setReport] = useState<AdaptiveReport | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [remaining, setRemaining] = useState<number | null>(null);
  const startedAt = useRef<number>(0);

  useEffect(() => {
    if (!ctx) return;
    (async () => {
      if (!navigator.onLine) {
        const pack = await getPack("quiz", quizId);
        if (!pack) { toast.error("Quiz unavailable offline. Download it first."); navigate({ to: "/learner/quizzes" }); return; }
        setQuiz(pack.data.quiz as any);
        setQuestions((pack.data.questions ?? []) as any);
        setAttemptCount(0);
        return;
      }
      const { data: q } = await supabase.from("quizzes")
        .select("id,title,description,instructions,time_limit_min,total_marks,pass_mark,difficulty,randomize_questions,shuffle_options,show_feedback,show_correct_answers,max_attempts,subject:subjects_catalog(name)")
        .eq("id", quizId).eq("school_id", ctx.schoolId).eq("published", true).maybeSingle();
      if (!q) { toast.error("Quiz unavailable"); navigate({ to: "/learner/quizzes" }); return; }
      const { data: qs } = await supabase.from("quiz_questions")
        .select("id,type,question,options,correct_answer,explanation,marks,difficulty,sort_order")
        .eq("quiz_id", quizId).order("sort_order");
      const { count } = await supabase.from("quiz_attempts").select("id", { count: "exact", head: true })
        .eq("quiz_id", quizId).eq("learner_id", ctx.learnerId).not("submitted_at", "is", null);
      setQuiz(q as any);
      setQuestions((qs ?? []) as any);
      setAttemptCount(count ?? 0);
    })();
  }, [ctx, quizId, navigate]);

  function begin() {
    if (!quiz) return;
    const indices = questions.map((_, i) => i);
    setOrder(quiz.randomize_questions ? shuffle(indices) : indices);
    setStarted(true);
    startedAt.current = Date.now();
    if (quiz.time_limit_min) setRemaining(quiz.time_limit_min * 60);
  }

  useEffect(() => {
    if (!started || remaining == null || result) return;
    if (remaining <= 0) { submit(); return; }
    const t = setTimeout(() => setRemaining((r) => (r ?? 0) - 1), 1000);
    return () => clearTimeout(t);
  }, [started, remaining, result]);

  async function submit() {
    if (!quiz || !ctx || submitting || result) return;
    setSubmitting(true);
    let auto = 0;
    let needsGrading = false;
    const detail: any[] = [];
    for (const q of questions) {
      const ans = answers[q.id] ?? null;
      const g = gradeQuestion(q, ans);
      if (g.needsGrading) needsGrading = true;
      auto += g.score;
      detail.push({ question_id: q.id, answer: ans, score: g.score, is_correct: g.isCorrect, needs_grading: g.needsGrading });
    }
    const total = quiz.total_marks || questions.reduce((s, q) => s + q.marks, 0) || 1;
    const percent = +((auto / total) * 100).toFixed(2);
    const passed = quiz.pass_mark != null ? percent >= quiz.pass_mark : null;
    const timeSpent = Math.round((Date.now() - startedAt.current) / 1000);
    const attemptRow = {
      id: newId(),
      quiz_id: quizId, learner_id: ctx.learnerId,
      started_at: new Date(startedAt.current).toISOString(),
      submitted_at: new Date().toISOString(),
      score: auto, total, percent, passed: passed ?? false,
      auto_score: auto, needs_grading: needsGrading,
      time_spent_sec: timeSpent,
      answers: detail, attempt_no: attemptCount + 1,
      question_order: order,
    };
    let data: { id: string } | null = null;
    if (!navigator.onLine) {
      // Offline: mark locally, queue the attempt, keep the learner moving.
      await enqueue({ op: "insert", table: "quiz_attempts", payload: attemptRow, user_id: ctx.learnerId, dedupeKey: `quiz_attempt:${attemptRow.id}` });
      await saveProgress(`quiz:${quizId}`, { completed: true, score: auto, total });
      setSubmitting(false);
      data = { id: attemptRow.id };
      toast.success("Marked on this device — your score sends when you're back online.");
    } else {
      const res = await supabase.from("quiz_attempts").insert(attemptRow as any).select("id").single();
      setSubmitting(false);
      if (res.error) {
        await enqueue({ op: "insert", table: "quiz_attempts", payload: attemptRow, user_id: ctx.learnerId, dedupeKey: `quiz_attempt:${attemptRow.id}` });
        toast.message("Saved offline — we'll send it shortly.");
        data = { id: attemptRow.id };
      } else {
        data = res.data as { id: string };
      }
    }
    // Award XP
    if (passed) {
      try {
        const { data: xp } = await supabase.from("learner_xp").select("points").eq("learner_id", ctx.learnerId).maybeSingle();
        const prevPoints = xp?.points ?? 0;
        const gain = Math.max(10, Math.round(percent / 2));
        const nextPoints = prevPoints + gain;
        await supabase.from("learner_xp").upsert({ learner_id: ctx.learnerId, points: nextPoints }, { onConflict: "learner_id" });
        const prevLevel = Math.max(1, Math.floor(Math.sqrt(prevPoints / 100)) + 1);
        const nextLevel = Math.max(1, Math.floor(Math.sqrt(nextPoints / 100)) + 1);
        if (nextLevel > prevLevel) {
          celebrate({ type: "level", title: `Level up! Level ${nextLevel}`, message: `+${gain} XP · ${percent}% on ${quiz.title ?? "quiz"}` });
        } else {
          celebrate({ type: "xp", title: `Quiz passed · +${gain} XP`, message: `${percent}% on ${quiz.title ?? "quiz"}` });
        }
      } catch { /* ignore */ }
    } else {
      celebrate({ type: "xp", title: `Quiz submitted`, message: `${percent}% — keep going!` });
    }
    setResult({ percent, auto, total, passed, needsGrading, detail, attemptId: data?.id });

    // Feed Learner Intelligence — per-question signals
    try {
      const subjectName = quiz.subject?.name ?? "General";
      const subjectKey = slug(subjectName);
      const signals = questions.map((q) => {
        const d = detail.find((x: any) => x.question_id === q.id);
        const outcome: "correct" | "partial" | "wrong" | "skipped" =
          !d || d.answer == null || d.answer === "" ? "skipped"
          : d.is_correct === true ? "correct"
          : d.score > 0 ? "partial"
          : "wrong";
        const topicKey = `${subjectKey}:quiz-${quizId}`;
        return {
          source: "quiz" as const,
          sourceRef: q.id,
          nodeKey: `${topicKey}:q-${q.id}`,
          nodeLabel: (q.question ?? "Question").slice(0, 80),
          level: "concept" as const,
          subject: subjectName,
          ancestors: [
            { key: subjectKey, label: subjectName, level: "subject" as const },
            { key: topicKey, label: quiz.title ?? "Quiz", level: "topic" as const },
          ],
          outcome,
          errorKind: outcome === "correct" ? null : ("unknown" as const),
          difficulty: q.difficulty === "easy" ? 0.3 : q.difficulty === "hard" ? 0.8 : 0.5,
          weight: Math.max(1, Math.min(3, (q.marks ?? 1) / 2)),
          meta: { quizId, questionType: q.type },
        };
      });
      sendSignals({ data: { signals } }).catch(() => {});
    } catch (e) { console.error("[intelligence] quiz submit", e); }

    // Publish to Makai OS so every other module can react (twin, tree, tutor,
    // teacher, parent). Fire-and-forget — it never blocks the learner.
    emitEvent({
      type: "assessment.scored",
      module: "exam_builder",
      learnerId: ctx.learnerId,
      subjectRef: quizId,
      severity: percent < 50 ? "attention" : "info",
      payload: {
        percent,
        subject: quiz.subject?.name ?? null,
        nodeLabel: quiz.title ?? "this quiz",
        quizId,
      },
    });



    // Fire AI Adaptive Learning Engine (best-effort)
    setAnalyzing(true);
    try {
      const correctCount = detail.filter((d) => d.is_correct === true).length;
      const incorrectCount = detail.filter((d) => d.is_correct === false).length;
      const skippedCount = detail.filter((d) => d.answer == null || d.answer === "").length;
      const subjectName = quiz.subject?.name ?? "General";
      const r = await analyze({
        data: {
          learnerId: ctx.learnerId,
          sourceType: "quiz",
          sourceId: quizId,
          subject: subjectName,
          topic: quiz.title,
          percent,
          durationSec: timeSpent,
          totalQuestions: questions.length,
          correctCount,
          incorrectCount,
          skippedCount,
          questions: questions.map((qq) => {
            const d = detail.find((x) => x.question_id === qq.id);
            return {
              prompt: qq.question,
              correct: d?.is_correct ?? null,
              skipped: d?.answer == null || d?.answer === "",
              topic: quiz.title,
              difficulty: (qq.difficulty as string) ?? quiz.difficulty,
              learner_answer: formatAnswer(qq, (d?.answer ?? null) as Answer),
              correct_answer: formatCorrect(qq),
            };
          }),
        },
      });
      setReport(r);
    } catch (err) {
      console.warn("Adaptive analysis failed", err);
    } finally {
      setAnalyzing(false);
    }

    // Generate AI Report Card (best-effort, background)
    try {
      const res = await genReport({
        data: {
          learnerId: ctx.learnerId,
          sourceType: "quiz",
          sourceRef: quizId,
          title: quiz.title ?? "Quiz",
          subject: quiz.subject?.name ?? null,
          totalMarks: total,
          score: auto,
          timeTakenSec: timeSpent,
          attempted: detail.filter((d) => d.answer != null && d.answer !== "").length,
          totalQuestions: questions.length,
          questions: questions.map((qq) => {
            const d = detail.find((x) => x.question_id === qq.id);
            return {
              prompt: qq.question,
              topic: quiz.title,
              correct: d?.is_correct ?? null,
              skipped: d?.answer == null || d?.answer === "",
              marks: qq.marks,
            };
          }),
        },
      });
      setReportId(res.id);
    } catch (err) {
      console.warn("Report generation failed", err);
    }
  }

  if (loading || !quiz) return <AppShell role="learner" title="Quiz"><div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin" /></div></AppShell>;

  const left = remaining != null ? `${Math.floor(remaining / 60).toString().padStart(2, "0")}:${(remaining % 60).toString().padStart(2, "0")}` : null;

  // Result screen
  if (result) {
    return (
      <AppShell role="learner" title={quiz.title} subtitle="Quiz results">
        <div className="rounded-3xl border border-border bg-gradient-to-br from-primary/10 via-card to-gold/10 p-6 text-center shadow-soft">
          <Trophy className={`mx-auto h-12 w-12 ${result.passed ? "text-gold" : "text-muted-foreground"}`} />
          <div className="mt-3 text-4xl font-display font-black">{result.percent}%</div>
          <div className="text-sm text-muted-foreground">{result.auto} / {result.total} marks</div>
          {result.passed != null && (
            <div className={`mt-2 inline-block rounded-full px-3 py-1 text-xs font-bold ${result.passed ? "bg-primary/15 text-primary" : "bg-clay/20 text-clay-foreground"}`}>
              {result.passed ? "Passed" : "Keep practicing"}
            </div>
          )}
          {result.needsGrading && <p className="mt-3 text-xs text-muted-foreground inline-flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Essay answers awaiting teacher grading.</p>}
          {reportId && (
            <div className="mt-4">
              <Link to="/learner/reports/$reportId" params={{ reportId }} className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
                <Sparkles className="h-4 w-4" /> View AI Report Card
              </Link>
            </div>
          )}
        </div>
        {analyzing && !report && (
          <div className="mt-6 rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4 animate-pulse text-primary" />
            MAKAI AI is analysing your performance…
          </div>
        )}
        {report && (
          <div className="mt-6">
            <AdaptiveReportView report={report} />
          </div>
        )}
        {quiz.show_correct_answers && (
          <div className="mt-6 space-y-3">
            {questions.map((q, i) => {
              const d = result.detail.find((x: any) => x.question_id === q.id);
              return (
                <div key={q.id} className="rounded-2xl border border-border bg-card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-sm font-semibold">Q{i + 1}. {q.question}</div>
                    {d.is_correct === true && <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />}
                    {d.is_correct === false && <XCircle className="h-5 w-5 shrink-0 text-clay" />}
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">Your answer: <span className="font-medium text-foreground">{formatAnswer(q, d.answer)}</span></div>
                  {d.is_correct === false && q.correct_answer && (
                    <div className="mt-1 text-xs text-muted-foreground">Correct: <span className="font-medium text-primary">{formatCorrect(q)}</span></div>
                  )}
                  {q.explanation && <div className="mt-2 rounded-lg bg-muted/50 p-2 text-xs">{q.explanation}</div>}
                </div>
              );
            })}
          </div>
        )}
        <div className="mt-6 flex gap-2">
          <Button asChild variant="outline" className="flex-1"><Link to="/learner/quizzes">Back to quizzes</Link></Button>
          {(!quiz.max_attempts || attemptCount + 1 < quiz.max_attempts) && (
            <Button className="flex-1" onClick={() => { setResult(null); setReport(null); setAnswers({}); setIdx(0); setStarted(false); setAttemptCount((c) => c + 1); }}>Try again</Button>
          )}
        </div>
      </AppShell>
    );
  }

  // Pre-start screen
  if (!started) {
    return (
      <AppShell role="learner" title={quiz.title}>
        <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
          {quiz.description && <p className="text-sm text-muted-foreground">{quiz.description}</p>}
          {quiz.instructions && <div className="mt-3 rounded-xl bg-muted/40 p-3 text-sm whitespace-pre-wrap">{quiz.instructions}</div>}
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <Stat label="Questions" value={questions.length} />
            <Stat label="Total marks" value={quiz.total_marks} />
            {quiz.time_limit_min && <Stat label="Time limit" value={`${quiz.time_limit_min} min`} />}
            {quiz.pass_mark != null && <Stat label="Pass mark" value={`${quiz.pass_mark}%`} />}
            <Stat label="Difficulty" value={quiz.difficulty} />
            {quiz.max_attempts && <Stat label="Attempts left" value={Math.max(0, quiz.max_attempts - attemptCount)} />}
          </div>
          <Button className="mt-6 w-full" disabled={questions.length === 0 || (quiz.max_attempts != null && attemptCount >= quiz.max_attempts)} onClick={begin}>
            Start quiz
          </Button>
          {questions.length === 0 && <p className="mt-2 text-center text-xs text-muted-foreground">No questions in this quiz yet.</p>}
        </div>
      </AppShell>
    );
  }

  // Playing
  const q = questions[order[idx]];
  const answered = order.filter((i) => answers[questions[i].id] !== undefined && answers[questions[i].id] !== "").length;

  return (
    <AppShell role="learner" title={quiz.title} subtitle={`Question ${idx + 1} of ${order.length}`}
      action={left ? <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-bold ${remaining! < 60 ? "bg-clay/20 text-clay-foreground" : "bg-primary/10 text-primary"}`}><Timer className="h-4 w-4" /> {left}</span> : undefined}>
      <div className="mb-3 h-1.5 overflow-hidden rounded-full bg-muted">
        <div className="h-full bg-primary transition-all" style={{ width: `${((idx + 1) / order.length) * 100}%` }} />
      </div>
      <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{QUESTION_TYPE_LABEL[q.type]} · {q.marks} {q.marks === 1 ? "mark" : "marks"}</div>
        <h3 className="mt-1 font-display text-lg font-bold">{q.question}</h3>
        <div className="mt-4">
          <QuestionInput q={q} value={answers[q.id]} onChange={(v) => setAnswers({ ...answers, [q.id]: v })} shuffle={quiz.shuffle_options} />
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button variant="outline" disabled={idx === 0} onClick={() => setIdx(idx - 1)}><ArrowLeft className="mr-1 h-4 w-4" /> Previous</Button>
        {idx < order.length - 1 ? (
          <Button onClick={() => setIdx(idx + 1)}>Next <ArrowRight className="ml-1 h-4 w-4" /></Button>
        ) : (
          <Button onClick={submit} disabled={submitting}><Send className="mr-1 h-4 w-4" /> {submitting ? "Submitting…" : "Submit"}</Button>
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {order.map((qi, i) => {
          const qq = questions[qi];
          const has = answers[qq.id] !== undefined && answers[qq.id] !== "";
          return (
            <button key={qi} onClick={() => setIdx(i)}
              className={`h-8 w-8 rounded-md text-xs font-bold ${i === idx ? "bg-primary text-primary-foreground" : has ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
              {i + 1}
            </button>
          );
        })}
      </div>
      <div className="mt-2 text-center text-xs text-muted-foreground">{answered}/{order.length} answered</div>
    </AppShell>
  );
}

function Stat({ label, value }: { label: string; value: any }) {
  return <div className="rounded-xl bg-muted/40 p-3"><div className="text-[10px] uppercase text-muted-foreground">{label}</div><div className="font-display text-lg font-bold capitalize">{value}</div></div>;
}

function QuestionInput({ q, value, onChange, shuffle: shuf }: { q: Question; value: Answer | undefined; onChange: (v: Answer) => void; shuffle: boolean }) {
  if (q.type === "mcq") {
    const opts: string[] = Array.isArray(q.options) ? q.options : [];
    const indexed = opts.map((o, i) => ({ o, i: String(i) }));
    const display = shuf ? shuffle(indexed, q.id.charCodeAt(0)) : indexed;
    return (
      <div className="space-y-2">
        {display.map(({ o, i }) => (
          <label key={i} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 ${value === i ? "border-primary bg-primary/5" : "border-border"}`}>
            <input type="radio" checked={value === i} onChange={() => onChange(i)} className="accent-primary" />
            <span className="text-sm">{o}</span>
          </label>
        ))}
      </div>
    );
  }
  if (q.type === "true_false") {
    return (
      <div className="grid grid-cols-2 gap-2">
        {["true", "false"].map((v) => (
          <button key={v} type="button" onClick={() => onChange(v)}
            className={`rounded-xl border p-4 text-sm font-bold capitalize ${value === v ? "border-primary bg-primary/10 text-primary" : "border-border"}`}>{v}</button>
        ))}
      </div>
    );
  }
  if (q.type === "fill_blank") {
    let blanks: string[] = [];
    try { blanks = JSON.parse(q.correct_answer ?? "[]"); } catch { blanks = [""]; }
    const arr: string[] = Array.isArray(value) ? value : new Array(blanks.length).fill("");
    return (
      <div className="space-y-2">
        {blanks.map((_, i) => (
          <Input key={i} placeholder={`Blank ${i + 1}`} value={arr[i] ?? ""}
            onChange={(e) => { const next = [...arr]; next[i] = e.target.value; onChange(next); }} />
        ))}
      </div>
    );
  }
  if (q.type === "matching") {
    const left: string[] = q.options?.left ?? [];
    const right: string[] = q.options?.right ?? [];
    const map = (typeof value === "object" && value && !Array.isArray(value)) ? value as Record<string, string> : {};
    return (
      <div className="space-y-2">
        {left.map((l, i) => (
          <div key={i} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
            <div className="rounded-lg bg-muted/40 px-3 py-2 text-sm">{l}</div>
            <span className="text-muted-foreground">→</span>
            <select value={map[i] ?? ""} onChange={(e) => onChange({ ...map, [i]: e.target.value })}
              className="rounded-lg border border-input bg-background px-2 py-2 text-sm">
              <option value="">Select…</option>
              {right.map((r, j) => <option key={j} value={String(j)}>{r}</option>)}
            </select>
          </div>
        ))}
      </div>
    );
  }
  if (q.type === "essay" || q.type === "short") {
    return <Textarea rows={q.type === "essay" ? 8 : 3} value={(value as string) ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="Type your answer…" />;
  }
  return null;
}

function formatAnswer(q: Question, ans: Answer): string {
  if (ans == null || ans === "") return "—";
  if (q.type === "mcq") return Array.isArray(q.options) ? (q.options[Number(ans)] ?? String(ans)) : String(ans);
  if (q.type === "fill_blank") return Array.isArray(ans) ? ans.join(" · ") : String(ans);
  if (q.type === "matching") {
    const right: string[] = q.options?.right ?? [];
    const left: string[] = q.options?.left ?? [];
    return Object.entries(ans as Record<string, string>).map(([k, v]) => `${left[Number(k)] ?? k} → ${right[Number(v)] ?? v}`).join(", ");
  }
  return String(ans);
}

function formatCorrect(q: Question): string {
  if (!q.correct_answer) return "—";
  if (q.type === "mcq") return Array.isArray(q.options) ? (q.options[Number(q.correct_answer)] ?? q.correct_answer) : q.correct_answer;
  if (q.type === "fill_blank") { try { return (JSON.parse(q.correct_answer) as string[]).join(" · "); } catch { return q.correct_answer; } }
  if (q.type === "matching") {
    try {
      const m = JSON.parse(q.correct_answer) as Record<string, string>;
      const right: string[] = q.options?.right ?? [];
      const left: string[] = q.options?.left ?? [];
      return Object.entries(m).map(([k, v]) => `${left[Number(k)] ?? k} → ${right[Number(v)] ?? v}`).join(", ");
    } catch { return q.correct_answer; }
  }
  return q.correct_answer;
}
